/**
 *      Xoom "Module" Format.
 *      Documentation: http://confluence/display/Eng/JavaScript+Module+Framework
 */
/* jslint es6 */
import $ from 'zepto';
import {
  random,
  $$$,
  registerDomObjects,
  getOffset,
} from '../../../utils/legacy';

import {
  getHighestZIndex,
  getPopupHeightTop,
  getPointerTopPosition,
  getZIndex,
  clickEventHandler,
  getSelectorFromDOMObject,
} from './popupsHelper';

import {
  TEMP_ELEMENT_ID,
  MOUSEOVER,
  MOUSEOUT,
  ACTIVE,
  CLICK,
  EMPTY_STRING,
  popupCache,
  popupTypes,
  defaultOptions,
  TITLE,
} from '../../../utils/legacy/constants';

import {
  POPUP_TEMPLATE,
} from '../../../utils/legacy/templateConstants';

import {
  customtargetEltUsedByCustomPulldownOnlyResetWidth,
} from '../../../utils/legacy/customPulldown';
import { isVisible } from '../../../utils';
import initAccessibility from './accessibility';

const windowBuffer = 20; // tooltip should be no less than 20px from the edge of the window
const popupEdgeTolerance = 20;
const windowHeight = $(window).height(); /* use supplied "window" element or the real window element */
const scrollTop = $(document).scrollTop();
const viewPortBottom = scrollTop + windowHeight;
let hideTimeout;

function clearPopupTimeout() {
  clearTimeout(hideTimeout);
}

function setRightPosition(targetSpecs, popupHeight, options) {
  const targetPosTop = targetSpecs.top;
  const targetPosLeft = targetSpecs.left;
  const targetEltHeight = targetSpecs.height;
  const targetEltWidth = targetSpecs.width;
  const popupHeightRight = popupHeight;
  let top = targetPosTop + (targetEltHeight / 2) - (popupHeightRight / 2);
  const popupBottom = top + popupHeightRight;
  const isTooLow = popupBottom > viewPortBottom;
  const isTooHigh = top < scrollTop;

  if (!options.positionAllowOutsideViewport) {
    if ((isTooLow && isTooHigh)) {
      // TODO: lock the height and add scrollbars?
      top = scrollTop;
    } else if (isTooLow) {
      top -= (popupBottom - viewPortBottom);
    } else if (isTooHigh) {
      top = scrollTop;
    }
  }

  return {
    position: {
      top,
      left: targetPosLeft + targetEltWidth,
    },
    pointer: {
      left: 0,
      top: (targetPosTop - top) + (targetEltHeight / 2),
    },
  };
}

function safeClearPopupTimeout(options) {
  if (options.persist) {
    clearPopupTimeout();
  }
}

function setBelowFold(isBelowFold, targetEltHeight, targetPosTop, customtargetEltUsedByCustomPulldownOnly, $popup) {
  const belowFold = {
    top: targetPosTop,
    popup: $popup.prepend(customtargetEltUsedByCustomPulldownOnly).addClass('above'),
    customtargetEltUsedByCustomPulldownOnlyLocationClass: 'custom-pulldown-popup-isabove',
  };

  if (isBelowFold) {
    belowFold.top += targetEltHeight;
    belowFold.popup = $popup.append(customtargetEltUsedByCustomPulldownOnly).addClass('below');
    belowFold.customtargetEltUsedByCustomPulldownOnlyLocationClass = 'custom-pulldown-popup-isbelow';
  }
  return belowFold;
}

function setActivePopupProps(positions, tooltipZIndex, $popup) {
  if ($popup.hasClass(ACTIVE)) {
    $popup.animate(positions, 200);
  } else {
    $popup.addClass(ACTIVE).css(positions).css('z-index', tooltipZIndex);
  }
}

function getTipLeftAndTopPosition($tipTarget, targetLeftCenter, positions,
  tipTargetLeft, tipTargetTop, tipTargetSelector) {
  const tipObject = {
    tipLeft: targetLeftCenter,
    top: positions.top,
  };

  if (tipTargetSelector !== null && $tipTarget !== null) {
    tipObject.tipLeft = tipTargetLeft + ($tipTarget.width() / 2);
    tipObject.top = tipTargetTop + $tipTarget.height();
  } else {
    tipObject.tipLeft = targetLeftCenter;
  }
  return tipObject;
}

function getHoverLeft(hoverLeft, popupWidth, windowWidth, targetPosLeft, tipLeft) {
  let hoverLeftVal = hoverLeft;
  if (hoverLeft < windowBuffer) {
    hoverLeftVal = windowBuffer;
  }

  if ((hoverLeft + popupWidth) > (windowWidth - windowBuffer)) {
    hoverLeftVal = (windowWidth - popupWidth) - windowBuffer;
    // is the popup going to be orphaned from the target?
    // orphaned = the popup is going to be so far left, that it will no longer be under the target
    // and any pointer will be pointing at nothing
    if ((hoverLeft + popupWidth - (popupEdgeTolerance * 2)) < targetPosLeft) {
      hoverLeftVal = tipLeft - popupWidth + (popupEdgeTolerance * 2);
    }
  }

  return hoverLeftVal;
}

// verify that the pointer is not orphaned from the box
// orphaned = we shouldn't blindly assume that the popup is perfectly centered
// underneath the target. Sometimes it's off centered due to screen/window restraints
// and due to the width of the target (which could be very narrow)
// If the pointerLeft is bigger than the width of the popup
// Then we're orphaned. Make the pointerLeft = the Width of the popup (minus the width of the pointer)
function getPointerLeft(pointerLeft, popupWidth) {
  if (pointerLeft > (popupWidth - popupEdgeTolerance)) {
    return popupWidth - popupEdgeTolerance;
  }
  return pointerLeft;
}

/**
 * Adjust the popup width if needed and return the new width
 *
 * @method adjustPopupWidth
 * @param {Number} targetEltWidth Target element width
 * @param {Number} windowWidth Window element width
 * @returns {Number} The adjusted popup width
 * */
function adjustPopupWidth(targetEltWidth, windowWidth, $popup, options) {
  let adjustedPopupWidth;

  // make popup non-display: none so that we can get the dimensions
  $popup.addClass('xpop-info-gathering');

  const popupWidth = $popup.width();

  if ((options.width === 'auto') && (popupWidth < targetEltWidth)) {
    // if we specify the width as "Auto" then set the popup width equal to the target elements width
    // AND do not allow the popup to be wider than the target
    adjustedPopupWidth = targetEltWidth;
  } else if (options.width === 'target') {
    // if we specify the width as "target" then set the popup width equal to the target elements width
    adjustedPopupWidth = targetEltWidth;
  } else if (options.width === 'window') {
    // if we specify the width as "window" then set
    // the popup width equal width of the windowWidth (up to max-width in CSS)
    adjustedPopupWidth = windowWidth;
  } else if (options.width) {
    // if the width was specified and the value is not any of the above, then just apply it to the element
    // then get the actual width number.  We apply to element first in case we are applying 'em' units
    // then we query the element to get the actual width in pixels
    $popup.width(options.width);
    adjustedPopupWidth = $popup.width();
  } else {
    // no special sizing set
    adjustedPopupWidth = popupWidth;
  }

  // Do not allow the popup to be wider than the window (minus the buffer/padding).
  if (adjustedPopupWidth >= (windowWidth - windowBuffer * 2)) {
    adjustedPopupWidth = windowWidth - (windowBuffer * 2);
  }

  // only adjust width if width has changed
  // save a DOM reflow
  if (popupWidth !== adjustedPopupWidth) {
    $popup.width(adjustedPopupWidth);
  }

  // hide the popup now that we have the data we needed
  $popup.removeClass('xpop-info-gathering');
  return adjustedPopupWidth;
}

/**
 set the popups content.
  @method setContent
  @private
  */
function setContent(content, $popup) {
  if (content) {
    $('.xpop-content', $popup).html(content);
  }
}

function getTargetElement(targetSelector) {
  // test to see if target element still exists on the DOM, if not,
  // this is needed when the target was dynamically replaced
  const targetElement = $(targetSelector)[0];
  const targetElementId = targetElement.id;
  let targetElementSelected = targetSelector;

  if (!$.contains(document.body, targetElement) && targetElementId) {
    // targetElementSelected was not attached to document
    // reset targetElementSelected by attempting to get the element based
    // on the detached elements ID
    targetElementSelected = $$$(targetElementId);
  }

  return targetElementSelected;
}

function getTipTargetSelector(options, tipTargetSelector) {
  if (options.tipTargetSelector) {
    return options.tipTargetSelector;
  }
  return tipTargetSelector;
}

/**
 bind hover events to the target element
 @method bindTargetToHoverEvent
 @private
 */
function bindTargetToHoverEvent($target, showPopup, hidePopup) {
  $target
    .bind(MOUSEOVER, () => {
      showPopup();
    })
    .bind(MOUSEOUT, hidePopup)
    .bind(CLICK, clickEventHandler);
}

function getPopup(popup, isPopupExists, popupIdElement, options, $target, hidePopup) {
  // if the popup doesn't already exist, then create it
  let $popup = popup;
  if (!isPopupExists) {
    let content = EMPTY_STRING;
    const titleAttr = $target.attr(TITLE);
    if (titleAttr) {
      $target.attr(TITLE, EMPTY_STRING);
      content = titleAttr;
    }
    $popup = $(POPUP_TEMPLATE.replaceTokens(popupIdElement, content, EMPTY_STRING));

    // append the popup to the body after we've add all of our classes and stuff
    // to save some DOM reflows
    $('body').append($popup);
  }

  if (!options.animate) {
    $popup.addClass('no-animate');
  }

  if (options.className) {
    $popup.addClass(options.className);
  }

  if (options.width) {
    $popup.css('width', options.width);
  }

  if (options.persist) {
    $popup
      .bind(MOUSEOVER, clearPopupTimeout)
      .bind(MOUSEOUT, hidePopup);
  }

  return $popup;
}

function getPositionTop(isBelowFold, positions, popupHeight, targetPosTop) {
  if (isBelowFold) {
    return targetPosTop - popupHeight; // flip the position to be on top of the target
  }
  return positions.top;
}

function setPointerValues($pointer, pointerLeft, pointerTopPosition) {
  $pointer.css({
    left: Math.max(0, pointerLeft - ($pointer.width() / 2)),
    top: pointerTopPosition,
  });
}

function setCloseBox(useCloseBox, $popup, hidePopup) {
  if (useCloseBox) {
    $('.xpop-close', $popup).bind('click', hidePopup);
    $popup.addClass('useCloseBox');
  }
}

function getPopupIdElement(popupId) {
  if (!popupId) {
    return TEMP_ELEMENT_ID + random(); // create random id if no popupId was specified
  }
  return popupId;
}

function getOptionsWidth(size, width, $popup) {
  if (size) {
    $popup.addClass(`size-${size}`);
    // size value of "full" means make the popup as large as the "window" (up to max-width set in CSS)
    if (size === 'full') {
      return 'window';
    }
  }
  return width;
}

function showHidePopup(targetSelector, showPopup, hidePopup) {
  if (isVisible(targetSelector)) {
    showPopup();
  } else {
    hidePopup();
  }
}

function getPosition(positionObject, targetEltHeight, targetEltWidth,
  popupHeight, options) {
  return setRightPosition({
    top: positionObject.targetPosTop,
    left: positionObject.targetPosLeft,
    height: targetEltHeight,
    width: targetEltWidth,
  }, popupHeight, options);
}

function addPublicMethods(publicMethods, $popup, $target, hidePopup) {
  const method = publicMethods;
  method.destroy = function destroy() {
    $popup.remove();
  };

  /**
    Get the target HTML Element
    @method getTarget
    @return {HTMLElement}
    @public
  */
  method.getTarget = function getTarget() {
    return $target;
  };

  /**
    Get the popup HTML Element
    @method get
    @return {HTMLElement}
    @public
  */
  method.get = function get() {
    return $popup;
  };
  method.abortHide = clearPopupTimeout;

  /**
    Hide this popup
    @method hide
    @public
  */
  method.hide = function hide() {
    hidePopup();
  };

  return method;
}

function getTargetSelector(newTargetElement, targetSelector) {
  if (newTargetElement) { // sometimes we no longer have the string selector; use newTargetElement instead
    targetSelector = $(newTargetElement); // eslint-disable-line no-param-reassign
  }
  return targetSelector;
}

function popupType(type, $popup) {
  if (type) {
    $popup.addClass(type.className);
  }
}
/*
  Popups
  This object is used to create consistent popups.
  Popups are Tooltips, Help icon tooltips, and other floating elements which have the appearance of a popup.
  Other code should use this object to create a popup. This allows one method to be responsible for tracking all open
  popups, allowing for repositioning when the page resizes.
  For example, xoom.form.InlineErrors implements xoom.ui.Popups

  Usage:There are several ways to use these popups. Examples can be seen on xoom-style-guide.html
*/
/**
 Popup object.
 @method Popup
 @param {String} popupId ID that will be assigned to the popup
 @param {String} targetSelector jQuery selector which defines the element that the popup will be anchored to
 @param {Object} newOptions Options hash
 @return {Object} instance of Popup
 @private
 */
function Popup(popupId, targetSelector, newOptions) {
  const options = $.extend({}, defaultOptions, newOptions || {});
  let $popup = $$$(popupId);
  let active = 0;
  const $target = $(targetSelector);
  let tipTargetSelector = null;
  const type = popupTypes[options.type];
  const thisPopup = this;

  function hidePopupCallback() {
    if (active) {
      $popup.removeClass(ACTIVE);
      active = 0;
      if (options.closeCallBack) {
        options.closeCallBack($popup);
      }
    }
  }

  /**
   Hide this popup.
   @method hidePopup
   @private
   */
  function hidePopup() {
    if (options.persist) {
      hideTimeout = setTimeout(() => {
        hidePopupCallback();
      }, 100);
    } else {
      hidePopupCallback();
    }
  }

  function onShow() {
    if (options.onShow && !active) {
      options.onShow.call(thisPopup, thisPopup, popupId);
    }
  }

  /**
   Show this popup.
   @method showPopup
   @private
   */
  function showPopup() {
    const targetElt = $(getTargetElement(targetSelector));
    const targetPos = getOffset(targetElt);
    // TODO revert to use jQuery offset method after upgrading to >= 1.8 (see note above)
    let $tipTarget = null;
    let tipTargetPos;
    let tipTargetTop;
    let tipTargetLeft;
    let tipLeft;
    const targetPosTop = targetPos.top;
    const targetPosLeft = targetPos.left;
    const targetEltHeight = targetElt.height() || '25px';
    const targetEltWidth = targetElt.width();
    const $viewPort = $(options.window || window);
    const windowWidth = $viewPort.width();
    const $pointer = $('.xpop-pointer', $popup);
    let pointerLeft = 0;
    let pointerTop = 0;
    let positions = {
      top: targetPosTop + targetEltHeight,
      left: targetPosLeft,
    };
    let tooltipZIndex = getHighestZIndex(targetElt, $popup, options);
    const popupHeight = $popup.height(); // get the popup height after CSS classes have been applied
    // adjust the popup width if needed and return the new width
    const popupWidth = adjustPopupWidth(targetEltWidth, windowWidth, $popup, options);
    tooltipZIndex = getZIndex(tooltipZIndex, options);

    if (tipTargetSelector !== null) {
      $tipTarget = $(tipTargetSelector, targetElt);
      tipTargetPos = $tipTarget.offset();
      tipTargetTop = tipTargetPos.top;
      tipTargetLeft = tipTargetPos.left;
    }

    // Should the popup be horizontally centered in relation to the target?
    if (options.align === 'center') {
      const targetLeftCenter = targetPosLeft + (targetEltWidth / 2);
      let hoverLeft = Math.max(0, targetLeftCenter - (popupWidth / 2));
      const tipObject = getTipLeftAndTopPosition($tipTarget, targetLeftCenter, positions,
        tipTargetLeft, tipTargetTop, tipTargetSelector);
      tipLeft = tipObject.tipLeft;
      positions.top = tipObject.top;
      hoverLeft = getHoverLeft(hoverLeft, popupWidth, windowWidth, targetPosLeft, tipLeft);
      positions.left = hoverLeft;
      pointerLeft = tipLeft - hoverLeft;
      getPointerLeft(pointerLeft, popupWidth);
    }
    positions.top = getPopupHeightTop(positions.top, options.position, targetPosTop, popupHeight);
    if (options.position === 'bottom') {
      // if NOT positionFixed and if the popup is going to appear below the fold,
      // then allow the popup to appear above the target
      if (!options.positionFixed) {
        const bottom = positions.top + popupHeight;
        const isBelowFold = bottom > viewPortBottom;
        positions.top = getPositionTop(isBelowFold, positions, popupHeight, targetPosTop);
        if (options.customPulldownTargetElement) {
          const customtargetEltUsedByCustomPulldownOnly = $(options.customPulldownTargetElement);
          $popup.removeClass('below above').children('.targetCopy').remove();
          const belowFold = setBelowFold(isBelowFold, targetEltHeight,
            targetPosTop, customtargetEltUsedByCustomPulldownOnly, $popup);
          positions.top = belowFold.top;
          $popup = belowFold.popup;
          customtargetEltUsedByCustomPulldownOnlyResetWidth(customtargetEltUsedByCustomPulldownOnly,
            belowFold.customtargetEltUsedByCustomPulldownOnlyLocationClass, tooltipZIndex, targetEltWidth);
        }
      }
    } else if (options.position === 'right') { // is the popup positioned to the right of the target?
      const positionObject = {
        targetPosTop,
        targetPosLeft,
      };
      const positionResults = getPosition(positionObject, targetEltHeight, targetEltWidth,
        popupHeight, options);
      positions = positionResults.position;
      pointerLeft = positionResults.pointer.left;
      pointerTop = positionResults.pointer.top;
    }
    safeClearPopupTimeout(options);
    setActivePopupProps(positions, tooltipZIndex, $popup);
    const pointerTopPosition = getPointerTopPosition(options.position, pointerTop, popupHeight, $pointer);
    setPointerValues($pointer, pointerLeft, pointerTopPosition);
    onShow();
    initAccessibility({ popupId, hidePopup, $targetElement: targetElt });
    active = 1;
  }

  function initPopup() {
    const isPopupExists = $popup.length > 0;
    const popupIdElement = getPopupIdElement(popupId);
    $popup = getPopup($popup, isPopupExists, popupIdElement, options, $target, hidePopup);
    options.width = getOptionsWidth(options.size, options.width, $popup);

    if (options.bindTargetHover) {
      bindTargetToHoverEvent($target, showPopup, hidePopup);
    }
    popupType(type, $popup);
    tipTargetSelector = getTipTargetSelector(options, tipTargetSelector);
    setContent(options.message, $popup);
    setCloseBox(options.closeBox, $popup, hidePopup);
  }

  let publicMethods = {
    /**
     Show this popup
     @method show
     @public
     */
    show(newTargetElement) {
      targetSelector = getTargetSelector(newTargetElement, targetSelector); // eslint-disable-line no-param-reassign
      if (!$(targetSelector).closest('body').length) {
        targetSelector = $(getSelectorFromDOMObject($(targetSelector)[0])); // eslint-disable-line no-param-reassign
      }
      showHidePopup(targetSelector, showPopup, hidePopup);
    },

    /**
     Update the contents of the popup.
     If popup is active, reposition the popup
     @method update
     @public
     */
    update(content) {
      setContent(content);
      if (active) {
        this.show();
      }
    },

    /**
     Is popup active
     @method isActive
     @return {boolean}
     @public
     */
    isActive() {
      return active;
    },

    /**
     Set/override an option
     @method setOption
     @param key the name of the option
     @param value the value to set
     @public
     */
    setOption(key, value) {
      options[key] = value;
    },
  };
  initPopup(); // initialize object
  publicMethods = addPublicMethods(publicMethods, $popup, $target, hidePopup);
  $.extend(this, publicMethods); // append public methods to "this" object, for use in some rare circumstances
  return publicMethods;
}

/**
 Add new popup and register the popup with xoom.UIManager
 @method addPopup
 @param {String} popupId ID that will be assigned to the popup
 @param {String} targetSelector jQuery selector which defines the element that the popup will be anchored to
 @param {Object} newOptions Options hash
 @return {Object} pointer to instance of newly created Popup object
 @private
 */
export function addPopup(popupId, targetSelector, newOptions) {
  const id = popupId || `xoom-popup-${random()}`;
  const options = $.extend({}, defaultOptions, newOptions || {});
  const newPopup = new Popup(popupId, targetSelector, options);
  popupCache[id] = newPopup;

  registerDomObjects(id, newPopup, options.scope, options.hideOnBodyClick);
  if (options.callback) {
    options.callback(newPopup);
  }
  return newPopup;
}

/**
 Extract ID from HREF
 @method getHashId
 @param {String} href  Href string which contains a hash and an element ID
 @return {String} returns all text after the hash
 @private
 */
export function getHashId(href) {
  let hash;
  if (href) {
    const parts = href.split('#');
    if (parts.length === 2) {
      [, hash] = parts;
    }
  }
  return hash;
}

/**
 Get the help box content from an ajax request
 @method getAjaxTooltipContent
 @param {Object} popup popup object
 @private
 */
export function getAjaxTooltipContent(popup) {
  // get ajax content
  // update popup
  $.ajax({
    url: (popup.getTarget()[0].href).ajaxifyUrl(),
    success(content) {
      popup.update(content);
      popup.setOption('onShow', null); // turn off this feature so that we don't get the ajax contents more than once
    },
  });
}

/**
 Create an element ID based on the URL
 @method getComputedId
 @param {String} href  Href string
 @return {String} returns string representation of URL with "/" replaced with "_"
 @private
 */
export function getComputedId(href) {
  const { location } = document;
  return href.replace(location.protocol, EMPTY_STRING).replace(location.host, EMPTY_STRING).replace(/\//gi, '_');
}

export function getPopupId(popupId) {
  if (!popupId) {
    return `tooltip${random()}`;
  }
  return popupId;
}

export function addRelExtension(rel, popupId) {
  if (rel && !(popupId.indexOf('-tooltip') >= 0)) {
    return rel;
  }
  return '';
}

export default {
  addPopup,
  getHashId,
  getAjaxTooltipContent,
  getComputedId,
  getPopupId,
  addRelExtension,
};
